import { useEffect, useState, useCallback } from 'react'
import { useAuthStore } from '../store/authStore'

export type Theme = 'light' | 'dark' | 'auto'

export function useTheme() {
  const { user } = useAuthStore()
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light')
  
  // Obtener el tema desde user.configuracion
  const getThemeFromUser = useCallback((): Theme => {
    if (!user?.configuracion) return 'light'
    
    try {
      const config = typeof user.configuracion === 'string' 
        ? JSON.parse(user.configuracion) 
        : user.configuracion
      
      return config.theme || 'light'
    } catch {
      return 'light'
    }
  }, [user?.configuracion])
  
  // Detectar preferencias del sistema
  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }, [])
  
  // Aplicar tema al documento
  const applyTheme = useCallback((theme: Theme) => {
    if (typeof document === 'undefined') return
    
    const root = document.documentElement
    const effective = theme === 'auto' ? getSystemTheme() : theme
    
    if (effective === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    
    setEffectiveTheme(effective)
  }, [getSystemTheme])
  
  // Efecto para aplicar tema cuando cambia la configuración del usuario
  useEffect(() => {
    const theme = getThemeFromUser()
    applyTheme(theme)
    
    // Escuchar cambios en las preferencias del sistema cuando el tema es automático
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = () => {
        applyTheme('auto')
      }
      
      // Aplicar tema inmediatamente
      handleChange()
      
      mediaQuery.addEventListener('change', handleChange)
      
      return () => {
        mediaQuery.removeEventListener('change', handleChange)
      }
    }
  }, [user?.configuracion, user, getThemeFromUser, applyTheme])
  
  // Aplicar tema inmediatamente al montar el componente y cuando cambia el usuario
  useEffect(() => {
    const theme = getThemeFromUser()
    applyTheme(theme)
  }, [user]) // Cuando cambia el usuario
  
  return {
    theme: getThemeFromUser(),
    effectiveTheme,
    applyTheme,
  }
}

